import { PlaylistItem } from "@components/Playlist/PlaylistsPageComponents/AddContent";
import { ScreenInfo } from "@components/screens/ScreenInfoPage";
import { useAuth } from "@hooks/useAuth";
import { env } from "@src/env.mjs";
import { AuthUser as User } from "@utils/authClient";
// import * as Sentry from "@sentry/nextjs";

export const initializeGzAnalytics = (onLoad?: () => void) => {
  const scriptUrl = env.NEXT_PUBLIC_GZ_ANALYTICS_SCRIPT_URL;

  if (!scriptUrl) return;

  // @ts-ignore
  if (!window.GZA_INITIALIZED) {
    const analytics = document.createElement("script");
    analytics.src = scriptUrl;
    analytics.async = true;
    document.body.appendChild(analytics);

    analytics.onload = () => {
      onLoad?.();
    };
  } else {
    onLoad?.();
  }
};

export const initializeAnalyticsWithDeviceId = (deviceId?: string | null) => {
  const systemId = env.NEXT_PUBLIC_GZ_ANALYTICS_SYSTEM_ID;

  if (!systemId) return;

  initializeGzAnalytics(() => {
    // @ts-ignore
    if (window?.GZAnalytics?.init) window.GZAnalytics.init(systemId, deviceId);
  });
};

type EventPayloads = {
  PageVisited: {
    page: string;
    pathname: string;
  },
  OpenedApp: {};
  OpenedPage: {
    page: string;
  },
  MagicLinkSent: {
    email: string;
  };
  SignInError: {
    email?: string;
    password?: string;
    error?: string;
  };
  SignUpError: {
    email?: string;
    error?: string;
  };
  LoggedOut: {
    userId?: string;
  };
  LoggedIn: {
    user: User;
  };
  UserRefreshed: {
    user: User;
  };

  LayoutCreated: {
    layoutId?: string;
  };
  LayoutCreateFailed: {
    layoutName: string;
    errorMessage: string;
  };
  LayoutUpdated: {
    layoutId?: string;
    layoutName: string;
  };
  LayoutUpdateFailed: {
    layoutId: string;
    layoutName: string;
    errorMessage: string;
  };
  LayoutDeleted: {
    layoutId: string;
  };
  LayoutDeleteFailed: {
    layoutId: string;
    errorMessage: string;
  };
  PlaylistCreated: {
    playlistId: string;
    playlistName: string;
  };
  PlaylistCreateFailed: {
    playlistName: string;
    errorMessage: string;
  };
  PlaylistDeleted: {
    playlistId: string;
  };
  PlaylistConvertedToSmart: {
    playlistId: string;
  };
  PlaylistUpdated: {
    playlistId: string;
    playlistName: string;
  };
  PlaylistUpdateFailed: {
    playlistId: string;
    playlistName: string;
    errorMessage: string;
  };
  LocationCreated: {
    locationId: string;
    locationName: string;
  };
  LocationUpdated: {
    locationId: string;
    locationName: string;
  };
  LocationCreateFailed: {
    locationName: string;
    errorMessage: string;
  };
  LocationUpdateFailed: {
    locationId: string;
    locationName: string;
    errorMessage: string;
  };
  ScreenAdded: {
    screenId?: string;
    screenName?: string;
    locationId?: string;
  };

  OpenedScreen: {
    screenId?: string;
    screenName?: string;
  };
  PairScreenClicked: {
    screenId?: string;
    screenName?: string;
  };
  ScreenInfoUpdated: {
    screenId: string;
    screenName: string;
    locationId: string | null;
    screenType: string | null;
  };
  ScreenContentDialogOpened: {
    screenId: string;
    screenName: string;
  };
  ScreenContentSelected: {
    screenId: string;
    screenName: string;
    selectedContentType: string;
    selectedContentId: string;
  };
  ScreenContentPreviewed: {
    screenId: string;
    screenName: string;
    previewedContentType: string;
    previewedContentId: string;
  };
  ScreenContentSelectionSkipped: {
    screenId: string;
    screenName: string;
  };
  ScreenContentSelectionDone: {
    screenId: string;
    screenName: string;
  };
  ContentSyncToggled: {
    screenId: string;
    screenName: string;
    locationId: string | null;
    contentSynced: boolean;
  };
  LocationDeleted: {
    locationId: string;
    locationName: string;
  };
  AssetUploaded: {
    fileNames: string[];
    fileCount: number;
  };
  AssetUpdated: {
    assetId: string;
    assetName: string;
  };
  AssetUpdateFailed: {
    assetId: string;
    assetName: string;
    errorMessage: string;
  };
  AssetDeleted: {
    assetId: string;
  };
  AssetDeleteFailed: {
    assetId: string;
    errorMessage: string;
  };
  VideoDeleted: {
    videoId: string;
  };
  VideoDeleteFailed: {
    videoId: string;
    errorMessage: string;
  };
  PublicAssetDeleted: {
    assetId: string;
  };
  PublicAssetDeleteFailed: {
    assetId: string;
    errorMessage: string;
  };
  PublicVideoDeleted: {
    videoId: string;
  };
  PublicVideoDeleteFailed: {
    videoId: string;
    errorMessage: string;
  };
  PublicTemplateDeleted: {
    templateId: string;
  };
  TemplateDeleted: {
    templateId: string;
  };
  PublicTemplateDeleteFailed: {
    templateId: string;
    errorMessage: string;
  };
  TemplateDeleteFailed: {
    templateId: string;
    errorMessage: string;
  };
  MusicChanged: {
    screenId?: string;
    musicId: string;
    musicName: string;
  };
  LeftRoom: {
    roomId?: string;
  };
  PlanChanged: {
    oldPlanId?: number;
    newPlanId: number;
    oldPlanName?: string;
    newPlanName?: string;
    oldAmount?: number;
    newAmount?: number;
    userId?: string;
    organizationId?: string;
  };
  PlanChangeAttemptFailed: {
    attemptedPlanId?: number;
    attemptedPlanName?: string;
    reason: string;
    userId?: string;
    organizationId?: string;
  };

  ScreenCreated: {
    screenInfo: ScreenInfo;
    screenId: string;
  };
  ScreenDeleted: {
    screenId: string;
    screenName: string;
    locationId: string;
    locationName: string;
    userId?: string;
    organizationId?: string;
  };
  ScreenUnpaired: {
    screenId: string;
    screenName: string;
    locationId: string;
    locationName: string;
    userId?: string;
    organizationId?: string;
  };
  ScreenUnpairFailed: {
    screenId: string;
    screenName: string;
    errorMessage: string;
  };
  OrganizationCreated: {
    organizationId: string;
    organizationName: string;
    creatorUserId?: string;
    creatorUserEmail?: string;
  };
  OrganizationCreationFailed: {
    organizationName: string;
    creatorUserId?: string;
    creatorUserEmail?: string;
    errorMessage?: string;
  };
  PlanCreated: {
    planName: string;
    amount: number;
    currency: string;
  };
  PlanUpdated: {
    planId: number;
    planName: string;
    updatedFields: string[];
  };
  PlanCreationFailed: {
    planName: string;
    errorMessage?: string;
  };
  PlanUpdateFailed: {
    planId: number;
    planName: string;
    errorMessage?: string;
  };
  UserPermissionsUpdated: {
    userId: string;
    permissions: string[];
  };
  UserPermissionsUpdateFailed: {
    userId: string;
    permissions: string[];
    errorMessage?: string;
  };
  AppInstalled: {
    appId: string;
    appName: string;
  };
  AppUninstalled: {
    appId: string;
    appName: string;
  };
  AppInstallFailed: {
    appId: string;
    appName: string;
    errorMessage?: string;
  };
  AppUninstallFailed: {
    appId: string;
    appName: string;
    errorMessage?: string;
  };
  AssetUploadFailed: {
    fileNames: string[];
    fileCount: number;
    errorMessage?: string;
  };
  LocationDeleteFailed: {
    locationId: string;
    locationName: string;
    errorMessage?: string;
  };
  PlaylistDeleteFailed: {
    playlistId: string;
    errorMessage?: string;
  };
  ScreenDeleteFailed: {
    screenId: string;
    screenName: string;
    locationId: string;
    locationName: string;
    userId?: string;
    organizationId?: string;
    errorMessage?: string;
  };

  ScreenContentAssigned: {
    screenId: string;
    screenName: string;
    contentType: string;
    contentId: string;
    contentName: string;
    userId?: string;
    organizationId?: string;
  };
  ScreenContentUnassigned: {
    screenId: string;
    screenName: string;
    userId?: string;
    organizationId?: string;
  };
  ScreenRestarted: {
    screenId: string;
    screenName: string;
    userId?: string;
    organizationId?: string;
  };
  UserProfileUpdated: {
    userId: string;
    organizationId?: string;
    updatedFields: string[];
  };
  UserProfileUpdateFailed: {
    userId?: string;
    organizationId?: string;
    error: string;
  };
  OrganizationMemberInvited: {
    invitedUserEmail: string;
    organizationId?: string;
    organizationName?: string;
    inviterUserId?: string;
    inviterUserEmail?: string;
    permissions: string[];
  };
  OrganizationMemberInvitationFailed: {
    invitedUserEmail: string;
    organizationId?: string;
    organizationName?: string;
    inviterUserId?: string;
    inviterUserEmail?: string;
    permissions: string[];
    error: string;
  };
  OrganizationMemberRemoved: {
    removedUserId: string;
    removedUserEmail: string;
    organizationId?: string;
    organizationName?: string;
    removerUserId?: string;
    removerUserEmail?: string;
  };
  OrganizationMemberRemovalFailed: {
    removedUserId: string;
    removedUserEmail: string;
    organizationId?: string;
    organizationName?: string;
    removerUserId?: string;
    removerUserEmail?: string;
    error: string;
  };
  OrganizationMemberPermissionsUpdated: {
    memberId: string;
    organizationId?: string;
    updatedPermissions: string[];
    updaterUserId?: string;
    updaterUserEmail?: string;
  };
  OrganizationMemberPermissionsUpdateFailed: {
    memberId: string;
    organizationId?: string;
    attemptedPermissions: string[];
    updaterUserId?: string;
    updaterUserEmail?: string;
    error: string;
  };
  UserDeleted: {
    userId: string;
    email?: string;
  };
  UserDeleteFailed: {
    userId: string;
    email?: string;
    errorMessage: string;
  };
  InvitationProcessed: {
    invitationId: string;
    organizationId: string;
    userId?: string;
    action: string;
  };
  InvitationAccepted: {
    invitationId: string;
    organizationId: string;
    userId?: string;
  };
  InvitationRejected: {
    invitationId: string;
    organizationId: string;
    userId?: string;
  };
  InvitationProcessingFailed: {
    token: string;
    action: string;
    userId?: string;
    error: string;
  };
  BillingSuccess: {
    message: string;
    userId: string;
    organizationId?: string;
  };
  BillingError: {
    message: string;
    userId: string;
    organizationId?: string;
  };
  PlaylistItemAddFailed: {
    playlistId: string;
    item: PlaylistItem;
    error: string;
  };
  PlaylistItemAdded: {
    playlistId: string;
    item: PlaylistItem;
  };
  PlaylistItemDeleteFailed: {
    playlistId: string;
    item: PlaylistItem;
    error: string;
  };
  PlaylistItemDeleted: {
    playlistId: string;
    item: PlaylistItem;
  };
  PlaylistOrderChangeFailed: {
    playlistId: string;
    error: string;
  };
  PlaylistOrderChanged: {
    playlistId: string;
  };
  ScreenCreateFailed: {
    screenInfo: ScreenInfo;
    error: string;
  };
  ScreenUpdateFailed: {
    screenInfo: ScreenInfo;
    error: string;
  };
  ScreenUpdated: {
    screenInfo: ScreenInfo;
  };
  MusicIntegrationDeleted: {
    screenId: string;
    zoneId: number;
  };
  MusicIntegrationDeleteFailed: {
    screenId: string;
    zoneId: number;
    error: string;
  };
  VideoUploaded: {
    videoUrl: string;
  };
  VideoUploadFailed: {
    errorMessage: string;
  };
  VideoUpdated: {
    videoId: string;
    videoName: string;
  };
  VideoUpdateFailed: {
    videoId: string;
    videoName: string;
    errorMessage: string;
  };
  MutationError: {
    queryKey: string[];
    variables: any;
    error: string;
  };
  MutationSuccess: {
    queryKey: string[];
    variables: any;
    data: any;
  };
  PluginThumbnailDeleted: {
    thumbnailId: string;
  };
  PluginThumbnailDeleteFailed: {
    thumbnailId: string;
    errorMessage: string;
  };
  PluginThumbnailUploaded: {
    fileNames: string[];
  };
  PluginThumbnailUploadFailed: {
    errorMessage: string;
  };
  TemplateUpdateFailed: {
    templateId?: string;
    templateName: string;
    errorMessage?: string;
  };
  TemplateCreated: {
    templateId: string;
  };
  TemplateUpdated: {
    templateId: string;
    templateName: string;
  };
  TemplateCreateFailed: {
    templateId?: string;
    templateName: string;
    errorMessage?: string;
  };
  FontDeleted: {
    fontId: number;
  };
  FontDeleteFailed: {
    fontId: number;
    errorMessage: string;
  };
  FontCreated: {
    fontId: number;
    fontName: string;
  };
  FontCreateFailed: {
    fontName: string;
    errorMessage: string;
  };
  FontUpdated: {
    fontId: number;
    fontName: string;
  };
  FontUpdateFailed: {
    fontId: number;
    fontName: string;
    errorMessage: string;
  };
  AppDeleted: {
    appId: string;
  };
  AppDeleteFailed: {
    appId: string;
    errorMessage: string;
  };
  AppCreated: {
    appId: string;
    appName: string;
  };
  AppCreateFailed: {
    appName: string;
    errorMessage: string;
  };
  AppUpdated: {
    appId: string;
    appName: string;
  };
  AppUpdateFailed: {
    appId: string;
    appName: string;
    errorMessage: string;
  };
  TemplateCategoryDeleted: {
    templateCategoryId: number;
  };
  TemplateCategoryDeleteFailed: {
    templateCategoryId: number;
    errorMessage: string;
  };
  TemplateCategoryCreated: {
    templateCategoryId: number;
    templateCategoryName: string;
  };
  TemplateCategoryCreateFailed: {
    templateCategoryName: string;
    errorMessage: string;
  };
  TemplateCategoryUpdated: {
    templateCategoryId: number;
    templateCategoryName: string;
  };
  TemplateCategoryUpdateFailed: {
    templateCategoryId: number;
    templateCategoryName: string;
    errorMessage: string;
  };
  LayoutSaved: {
    layoutId: string;
    layoutName: string;
  };
  LayoutSaveFailed: {
    layoutId: string;
    layoutName: string;
    error: string;
  };
  ThumbnailSaved: {
    layoutId: string;
  };
  ThumbnailSaveFailed: {
    layoutId: string;
    error: string;
  };
};
type gzLogProps<K extends keyof EventPayloads> = {
  name: K;
  eventtype:
  | "Auth"
  | "InvitationAccepted"
  | "InvitationRejected"
  | "LocationUpdateFailed"
  | "LocationCreateFailed"
  | "PageVisited"
  | "Action"
  | "Clicked"
  | "APICall"
  | "APISuccess"
  | "APIFailure"
  | "SocketEvent"
  | "SocketListenEvent"
  | "SocketEmitEvent"
  | "JourneyTrack"
  | "MusicChanged"
  | "PlaylistDeleted"
  | "PlaylistConvertedToSmart"
  | "PlaylistCreated"
  | "PlaylistCreateFailed"
  | "PlaylistUpdated"
  | "PlaylistUpdateFailed"
  | "LocationCreated"
  | "PlanChanged"
  | "PlanChangeAttemptFailed"
  | "ScreenCreated"
  | "ScreenDeleted"
  | "ScreenDeleteFailed"
  | "ScreenUnpaired"
  | "ScreenUnpairFailed"
  | "ScreenRestarted"
  | "ScreenContentAssigned"
  | "ScreenContentUnassigned"
  | "UserProfileUpdated"
  | "UserProfileUpdateFailed"
  | "OrganizationMemberInvited"
  | "OrganizationMemberInvitationFailed"
  | "OrganizationMemberRemoved"
  | "OrganizationMemberRemovalFailed"
  | "OrganizationMemberPermissionsUpdated"
  | "OrganizationMemberPermissionsUpdateFailed"
  | "LocationUpdated"
  | "LocationCreateFailed"
  | "AssetUpdated"
  | "AssetUpdateFailed"
  | "AssetUploaded"
  | "AssetDeleted"
  | "AssetDeleteFailed"
  | "ContentSyncToggled"
  | "ScreenContentDialogOpened"
  | "ScreenContentSelected"
  | "ScreenContentPreviewed"
  | "ScreenContentSelectionSkipped"
  | "ScreenContentSelectionDone"
  | "LocationDeleted"
  | "PlanCreated"
  | "PlanUpdated"
  | "PlanCreationFailed"
  | "PlanUpdateFailed"
  | "UserPermissionsUpdated"
  | "UserPermissionsUpdateFailed"
  | "TemplateDeleted"
  | "AppInstalled"
  | "AppUninstalled"
  | "AppInstallFailed"
  | "AppUninstallFailed"
  | "AssetUploadFailed"
  | "LocationDeleteFailed"
  | "PlaylistDeleteFailed"
  | "ScreenDeleteFailed"
  | "TemplateDeleteFailed"
  | "UserDeleted"
  | "UserDeleteFailed"
  | "VideoDeleted"
  | "VideoDeleteFailed"
  | "TemplateDeleted"
  | "OrganizationCreated"
  | "OrganizationCreationFailed"
  | "PublicTemplateDeleteFailed"
  | "PublicTemplateDeleted"
  | "PublicVideoDeleted"
  | "PublicVideoDeleteFailed"
  | "PublicAssetDeleteFailed"
  | "PublicAssetDeleted"
  | "TemplateDeleted"
  | "VideoDeleted"
  | "VideoDeleteFailed"
  | "InvitationAccepted"
  | "InvitationRejected"
  | "InvitationProcessed"
  | "InvitationProcessingFailed"
  | "BillingSuccess"
  | "BillingError"
  | "PlaylistItemAddFailed"
  | "PlaylistItemAdded"
  | "PlaylistItemDeleteFailed"
  | "PlaylistItemDeleted"
  | "PlaylistOrderChangeFailed"
  | "PlaylistOrderChanged"
  | "ScreenCreateFailed"
  | "ScreenUpdateFailed"
  | "ScreenUpdated"
  | "MusicIntegrationDeleted"
  | "MusicIntegrationDeleteFailed"
  | "VideoUploaded"
  | "VideoUploadFailed"
  | "VideoUpdated"
  | "VideoUpdateFailed"
  | "MutationError"
  | "MutationSuccess"
  | "PluginThumbnailDeleted"
  | "PluginThumbnailDeleteFailed"
  | "PluginThumbnailUploaded"
  | "PluginThumbnailUploadFailed"
  | "TemplateUpdateFailed"
  | "TemplateCreated"
  | "TemplateUpdated"
  | "TemplateCreateFailed"
  | "FontDeleted"
  | "FontDeleteFailed"
  | "FontCreated"
  | "FontCreateFailed"
  | "FontUpdated"
  | "FontUpdateFailed"
  | "AppDeleted"
  | "AppDeleteFailed"
  | "AppCreated"
  | "AppCreateFailed"
  | "AppUpdated"
  | "AppUpdateFailed"
  | "TemplateCategoryDeleted"
  | "TemplateCategoryDeleteFailed"
  | "TemplateCategoryCreated"
  | "TemplateCategoryCreateFailed"
  | "TemplateCategoryUpdated"
  | "TemplateCategoryUpdateFailed"
  | "LayoutCreated"
  | "LayoutCreateFailed"
  | "LayoutUpdated"
  | "LayoutUpdateFailed"
  | "LayoutDeleted"
  | "LayoutDeleteFailed"
  | "LayoutSaved"
  | "LayoutSaveFailed"
  | "ThumbnailSaved"
  | "ThumbnailSaveFailed"
  | "LocationUpdateFailed"
  | "LocationCreateFailed"
  payload?: EventPayloads[K];
};

function logAnalytics(props: Record<string, any>) {
  // @ts-ignore
  if (window.GZA_INITIALIZED) {
    if (process.env.NEXT_PUBLIC_IS_DEV) {
      // eslint-disable-next-line no-console
      console.log(props);
    }
    //@ts-ignore
    if (window?.GZAnalytics?.customLog) window.GZAnalytics.customLog({ ...props, time: new Date().toISOString() });
  }
  // Sentry.captureMessage(JSON.stringify(props));
}

export const gzLog = <K extends keyof EventPayloads>(props: gzLogProps<K>) => {
  logAnalytics(props);
};

export default function useGzLog() {
  const { user } = useAuth();
  const log = <K extends keyof EventPayloads>(props: gzLogProps<K>) => {
    const logPayload: Record<string, any> = props.payload ?? {};
    if (!("user_id" in logPayload)) logPayload["user_id"] = user?.id;
    if (!("user_email" in logPayload))
      logPayload["user_email"] = btoa(user?.email ?? "");
    if (!("user_org_id" in logPayload))
      logPayload["user_org_id"] = user?.org_id;

    logAnalytics({
      ...props,
      payload: logPayload,
    });
  };
  return {
    log,
  };
}